/**
 * The Ask cap before a card (owner, 2026-10-14: "before a business adds a card, cap Ask; everything else stays as it is: safety
 * always free, guides, library and reading records free, records held for the card"). One pure rule for the server (routes/ask.ts,
 * the authority), the web and the phone.
 *
 * | AC1 Who     | A business with no card (planAccess level "setup" that holds its records: no plan, an incomplete first payment). Never the sample
 * |             | café (the App Review account), a test account, a running plan or its grace, a home, a place waiting on its county (no card can be
 * |             | added there), or a place a preparer is setting up (prep: nobody is asking it for a card). All of that is planAccess itself. |
 * | AC2 How many| ASK_FREE_QUESTIONS in total, per business, counted on the server. The demo's own number (owner, 2026-10-13: 2 sample questions;
 * |             | apps/web lib/demoTour.ts DEMO_ASK_CAP is this constant). |
 * | AC3 Safety  | Never walled, never counted (`askIsSafety`): illness (core askfeedback.ts isPrivateQuestion), every "What do I do if…" moment
 * |             | (core moments.ts: a warm fridge, a pest, a spill, a cut or burn, fire, no power, someone sick), a question asked from a report,
 * |             | and the few emergencies the moments do not name (a person choking, 911). |
 * | AC4 After   | The best matching guides still show (free), plus the one ask for the card, in the words of the audience (`askCapAsk`). |
 * | AC5 Count   | "N free questions left" only when 2 or fewer are left (`askLeftLine`), never on every answer. |
 */
import { INCIDENT_TYPES } from "./incidents.ts";
import { allMoments, momentFor } from "./moments.ts";
import { isPrivateQuestion } from "./askfeedback.ts";
import { ALWAYS_SAVES_TEMPLATES, type PlanAccess } from "./paywall.ts";
import type { AskAnswer, AskContext } from "./ask.ts";
import { closestGuides } from "./ask.ts";

/** Free questions in total before a card. The demo's number (owner, 2026-10-13). */
export const ASK_FREE_QUESTIONS = 2;
/** The count shows only in the last this-many. */
export const ASK_LEFT_SHOWS_AT = 2;

/** Does the cap apply to this business right now? (AC1: planAccess already excludes the sample, test accounts, running plans, homes.) */
export const askCapApplies = (a: Pick<PlanAccess, "level" | "reason" | "holdsRecords">): boolean =>
  a.level === "setup" && a.holdsRecords === true && a.reason !== "prep";

/** Emergencies the "What do I do if…" moments don't name. Short on purpose; the moments and the illness rule do the rest. */
const EMERGENCY = /\b(911|ambulance|choking|choked|chokes|unconscious|passed out|fainted|not breathing|can'?t breathe|cannot breathe|seizure|poison(ed|ing)?|bleeding (a lot|badly|heavily)|heart attack|overdose)\b/i;

/** A safety question: never capped and never counted (AC3). */
export function askIsSafety(question: string, ctx: Pick<AskContext, "incidentType" | "templateId"> = {}): boolean {
  // Only the app's own report kinds count (audit R21-02): any other string from a client steers nothing.
  if (ctx.incidentType && INCIDENT_TYPES.some((t) => t.type === ctx.incidentType)) return true;
  if (ctx.templateId && ALWAYS_SAVES_TEMPLATES.includes(ctx.templateId)) return true;
  const q = question.replace(/[’]/g, "'");
  return isPrivateQuestion(q) || EMERGENCY.test(q) || !!momentFor(q, allMoments());
}

/** The words, one place. The phone never names a price or a card button on a store build (App Store 3.1.3 / 3.1.1(a)). */
export const ASK_CAP_WORDS = {
  title: "You've used your free questions",
  /** On the answer, above the guides. */
  answer: "You've used your free questions. Here are the guides that fit.",
  /** Web, and the phone with checkout on: the one ask. */
  addCard: "Add your card to keep asking",
  /** Staff (no card rights): who adds it. */
  askOwner: (owner: string | null) => `Ask ${owner ?? "the owner"} to add a card to keep asking.`,
  /** Android, and iOS without the link: plain facts, no "add your card", no link, no price (3.1.3(f)). */
  phoneStore: "Asking more starts when your plan is running. Your plan is managed on the web.",
  /** Under the ask: what stays free. */
  stillFree: "Guides, the library and your records stay open, and safety questions are always answered.",
} as const;

/** Which ask a person sees: the button to the card, or words only (AC4). */
export type AskCapAsk = { kind: "card"; label: string } | { kind: "words"; text: string };
/** `card`: this audience may add the card here (web owner/admin; phone owner/admin on a US iOS build with checkout on). */
export function askCapAsk(o: { canManage: boolean; ownerName: string | null; /** Web, or a phone build with checkout on. */ cardHere: boolean; /** A store build without the link. */ storeBuild: boolean }): AskCapAsk {
  if (!o.canManage) return { kind: "words", text: ASK_CAP_WORDS.askOwner(o.ownerName) };
  if (o.cardHere) return { kind: "card", label: ASK_CAP_WORDS.addCard };
  return { kind: "words", text: o.storeBuild ? ASK_CAP_WORDS.phoneStore : ASK_CAP_WORDS.askOwner(o.ownerName) };
}

/** "2 free questions left" in the last 2 only (AC5); nothing before, and the last one is said once. */
export function askLeftLine(left: number): string | undefined {
  if (left < 0 || left > ASK_LEFT_SHOWS_AT) return undefined;
  if (left === 0) return "That was your last free question.";
  return `${left} free ${left === 1 ? "question" : "questions"} left`;
}

/** What the server adds to an answer under the cap: `left` after a counted question, `capped` when this one was not answered. */
export interface AskCapInfo { cap: number; left: number; capped?: true }

/** The answer past the cap (AC4): the best matching guides, nothing from a model, nothing kept. */
export function askCappedAnswer(question: string, ctx: AskContext = {}): AskAnswer {
  return {
    answer: ASK_CAP_WORDS.answer, steps: [], fixes: [], sources: [], grounded: false, engine: "catalog",
    next: { guides: closestGuides(question, ctx), request: false, county: false },
    askCap: { cap: ASK_FREE_QUESTIONS, left: 0, capped: true },
  };
}
